import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { pluriel } from '@/lib/pluriel';
import { AdminButton } from '@/components/admin/button';
import { Card } from '@/components/admin/card';
import { CategoryPicker } from '@/components/admin/category-picker';
import { AdminIcon } from '@/components/admin/icon';
import { PageHead } from '@/components/admin/page-head';
import { Pagination } from '@/components/admin/pagination';
import { Tag } from '@/components/admin/tag';
import { useConfirm, useToast } from '@/components/ui/feedback';
import { Modal, ModalFooter } from '@/components/ui/modal';
import {
  useBrands,
  useCategoriesList,
  useHubs,
  useProductsList,
  useProductStats,
} from '@/hooks/use-catalog';
import { useLocalPref } from '@/hooks/use-local-pref';
import { usePageTitle } from '@/hooks/use-page-title';
import { importExportApi, productsApi } from '@/lib/catalog/catalog-api';
import type { Category, Product, ProductStatus } from '@/lib/catalog/types';
import { fmtFCFA } from '@/lib/format';
import { statusProduct } from '@/lib/status';
import { useMarketingCart } from '@/stores/marketing-cart.store';

const STATUS_OPTIONS: { value: ProductStatus | ''; label: string }[] = [
  { value: '', label: 'Tous les statuts' },
  { value: 'draft', label: 'Brouillon' },
  { value: 'published', label: 'Publié' },
  { value: 'paused', label: 'En pause' },
  { value: 'archived', label: 'Archivé' },
];

const KIND_OPTIONS: { value: '' | 'simple' | 'pack'; label: string }[] = [
  { value: '', label: 'Tous les types' },
  { value: 'simple', label: 'Produits simples' },
  { value: 'pack', label: 'Packs' },
];

const STOCK_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Tout stock' },
  { value: 'in', label: 'En stock (≥10)' },
  { value: 'low', label: 'Stock bas (<10)' },
  { value: 'out', label: 'Épuisé (0)' },
];

const SORT_OPTIONS: { value: string; label: string }[] = [
  { value: 'recent', label: 'Trier : Récents' },
  { value: 'price-asc', label: 'Prix croissant' },
  { value: 'price-desc', label: 'Prix décroissant' },
  { value: 'stock-asc', label: 'Stock croissant' },
  { value: 'name', label: 'Nom A→Z' },
];

// Préférences d'affichage (persistées en localStorage).
const GRID_COLS_OPTIONS = [2, 3, 4, 5, 6];
const PAGE_SIZE_OPTIONS = [12, 25, 50, 100];

const productTotalStock = (p: Product): number =>
  (p.variants ?? []).reduce(
    (acc, v) => acc + (v.stock ?? []).reduce((a, s) => a + s.quantity, 0),
    0,
  );

const productEffectivePrice = (p: Product): number => {
  const offerPrices = (p.variants ?? [])
    .flatMap((v) => v.offers ?? [])
    .filter((o) => o.status === 'active')
    .map((o) => o.price);
  if (offerPrices.length) return Math.min(...offerPrices);
  return p.basePrice;
};

const statusToBadge = (status: ProductStatus): ReturnType<typeof statusProduct> => {
  if (status === 'published') return statusProduct('published');
  if (status === 'draft') return statusProduct('draft');
  if (status === 'paused') return { l: 'En pause', cls: 'warning' };
  return { l: 'Archivé', cls: 'neutral' };
};

const stockClass = (s: number): 'ok' | 'low' | 'out' => (s === 0 ? 'out' : s < 10 ? 'low' : 'ok');
const stockLabel = (s: number): string =>
  s === 0 ? 'Épuisé' : s < 10 ? `${s} restant${s > 1 ? 's' : ''}` : `${s} en stock`;

/** Pictogramme produit fallback (style "abstrait coloré"). */
function ProdThumb({ color }: { color: string }) {
  return (
    <svg viewBox="0 0 200 200" preserveAspectRatio="xMidYMid slice">
      <rect width="200" height="200" fill={`${color}14`} />
      <circle cx="100" cy="116" r="58" fill={color} />
      <circle cx="100" cy="116" r="34" fill={`${color}40`} />
      <rect x="86" y="36" width="28" height="52" rx="7" fill={color} opacity="0.7" />
    </svg>
  );
}

/** Couleur déterministe à partir du SKU (palette Akwaba). */
const PALETTE = ['#C6563A', '#1E4D3B', '#D4A24C', '#2B2A26', '#8F3B26', '#A04428', '#3A6A8C'];
const colorFromSku = (sku: string): string => {
  let h = 0;
  for (let i = 0; i < sku.length; i++) h = (h * 31 + sku.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
};

interface DropdownOption {
  value: string;
  label: string;
}

interface DropdownProps {
  id: string;
  label: string;
  value: string;
  options: DropdownOption[];
  openId: string | null;
  setOpenId: (id: string | null) => void;
  onChange: (value: string) => void;
}

function Dropdown({ id, label, value, options, openId, setOpenId, onChange }: DropdownProps) {
  const active = value !== '';
  const current = options.find((o) => o.value === value);
  return (
    <div className="dd-wrap" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className={'chip-filter' + (active ? ' on' : '')}
        onClick={() => setOpenId(openId === id ? null : id)}
      >
        {active ? (current?.label ?? label) : label}
        <AdminIcon n="chevD" s={12} />
      </button>
      {openId === id && (
        <div className="dd-menu">
          {options.map((o) => (
            <button
              key={o.value || 'all'}
              type="button"
              className={'dd-item' + (value === o.value ? ' on' : '')}
              onClick={() => {
                onChange(o.value);
                setOpenId(null);
              }}
            >
              {value === o.value && <AdminIcon n="check" s={12} />}
              <span style={{ marginLeft: value === o.value ? 0 : 18 }}>{o.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function AdminCatalogPage() {
  const navigate = useNavigate();
  usePageTitle('Catalogue');
  // Vue, filtres et tri persistés en localStorage (la recherche reste volatile).
  const [view, setView] = useLocalPref<'table' | 'grid'>('akwaba_admin.catalog.view', 'grid');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useLocalPref<ProductStatus | ''>(
    'akwaba_admin.catalog.status',
    '',
  );
  const [categoryFilter, setCategoryFilter] = useLocalPref<string>(
    'akwaba_admin.catalog.category',
    '',
  );
  const [hubFilter, setHubFilter] = useLocalPref<string>('akwaba_admin.catalog.hub', '');
  const [brandFilter, setBrandFilter] = useLocalPref<string>('akwaba_admin.catalog.brand', '');
  const [stockFilter, setStockFilter] = useLocalPref<string>('akwaba_admin.catalog.stock', '');
  const [kindFilter, setKindFilter] = useLocalPref<'' | 'simple' | 'pack'>(
    'akwaba_admin.catalog.kind',
    '',
  );
  const [sort, setSort] = useLocalPref<string>('akwaba_admin.catalog.sort', 'recent');
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  // Préférences d'affichage persistées (clampées : le localStorage peut contenir n'importe quoi).
  const [gridColsPref, setGridColsPref] = useLocalPref<number>('akwaba_admin.catalog.gridCols', 4);
  const [pageSizePref, setPageSizePref] = useLocalPref<number>('akwaba_admin.catalog.pageSize', 25);
  const gridCols = GRID_COLS_OPTIONS.includes(gridColsPref) ? gridColsPref : 4;
  const pageSize = PAGE_SIZE_OPTIONS.includes(pageSizePref) ? pageSizePref : 25;

  // Fermer dropdowns au clic externe
  useEffect(() => {
    const close = () => setOpenDropdown(null);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, []);

  const params = useMemo(
    () => ({
      page,
      pageSize,
      search: search.trim() || undefined,
      status: statusFilter || undefined,
      kind: kindFilter || undefined,
      categoryId: categoryFilter || undefined,
      hubId: hubFilter || undefined,
      brandId: brandFilter || undefined,
    }),
    [page, pageSize, search, statusFilter, kindFilter, categoryFilter, hubFilter, brandFilter],
  );

  const { data: list, isLoading } = useProductsList(params);
  const { data: stats } = useProductStats();
  const { data: categories } = useCategoriesList({ isActive: true });
  const { data: hubs } = useHubs();
  const { data: brands } = useBrands();

  const rawItems = useMemo(() => list?.items ?? [], [list]);
  const total = list?.total ?? 0;

  // Filtrage stock + tri (client-side car back ne gère pas ces critères)
  const items = useMemo(() => {
    let arr = rawItems.slice();
    if (stockFilter) {
      arr = arr.filter((p) => {
        const stk = productTotalStock(p);
        if (stockFilter === 'in') return stk >= 10;
        if (stockFilter === 'low') return stk > 0 && stk < 10;
        if (stockFilter === 'out') return stk === 0;
        return true;
      });
    }
    if (sort === 'price-asc')
      arr.sort((a, b) => productEffectivePrice(a) - productEffectivePrice(b));
    else if (sort === 'price-desc')
      arr.sort((a, b) => productEffectivePrice(b) - productEffectivePrice(a));
    else if (sort === 'stock-asc') arr.sort((a, b) => productTotalStock(a) - productTotalStock(b));
    else if (sort === 'name') arr.sort((a, b) => a.title.localeCompare(b.title, 'fr'));
    return arr;
  }, [rawItems, stockFilter, sort]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  // ─── Sélection multiple + actions de masse ────────────────────────────────
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const [exporting, setExporting] = useState(false);
  const runExport = async () => {
    setExporting(true);
    try {
      await importExportApi.downloadExport({
        search: search.trim() || undefined,
        status: statusFilter || undefined,
        categoryId: categoryFilter || undefined,
        hubId: hubFilter || undefined,
        brandId: brandFilter || undefined,
      });
    } catch {
      toast.error('Export impossible');
    } finally {
      setExporting(false);
    }
  };
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkCatOpen, setBulkCatOpen] = useState(false);
  const [bulkCatId, setBulkCatId] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{
    done: number;
    total: number;
    label: string;
  } | null>(null);

  const toggleOne = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) {
        n.delete(id);
      } else {
        n.add(id);
      }
      return n;
    });
  const allVisibleSelected = items.length > 0 && items.every((p) => selected.has(p.id));
  const toggleAllVisible = () =>
    setSelected((s) => {
      const n = new Set(s);
      if (items.every((p) => n.has(p.id))) items.forEach((p) => n.delete(p.id));
      else items.forEach((p) => n.add(p.id));
      return n;
    });
  const clearSelection = () => setSelected(new Set());

  const addToMarketingCart = useMarketingCart((s) => s.addMany);
  /** Ajoute les produits sélectionnés au panier marketing (génération de pub/publication). */
  const addSelectedToMarketing = () => {
    const chosen = rawItems.filter((p) => selected.has(p.id));
    if (!chosen.length) return;
    addToMarketingCart(
      chosen.map((p) => ({
        id: p.id,
        title: p.title,
        image: p.mediaLinks?.[0]?.url ?? null,
        price: productEffectivePrice(p),
        currency: p.currency,
      })),
    );
    toast.success(
      `${chosen.length} produit${chosen.length > 1 ? 's ajoutés' : ' ajouté'} au panier marketing.`,
    );
    clearSelection();
  };

  /** Exécute `fn` sur chaque produit sélectionné (séquentiel, tolérant aux échecs). */
  const runBulk = async (fn: (id: string) => Promise<unknown>, label: string) => {
    const ids = [...selected];
    setBulkBusy(true);
    setBulkProgress({ done: 0, total: ids.length, label });
    let ok = 0;
    let fail = 0;
    for (const id of ids) {
      try {
        await fn(id);
        ok++;
      } catch {
        fail++;
      }
      setBulkProgress((pr) => (pr ? { ...pr, done: pr.done + 1 } : pr));
    }
    setBulkProgress(null);
    setBulkBusy(false);
    clearSelection();
    await qc.invalidateQueries({ queryKey: ['catalog', 'products'] });
    if (fail) toast.error(`${label} : ${pluriel(ok, 'réussite')}, ${pluriel(fail, 'échec')}.`);
    else toast.success(`${label} terminé : ${ok} produit${ok > 1 ? 's' : ''}.`);
  };

  const bulkArchive = async () => {
    const ok = await confirm({
      title: 'Archiver la sélection',
      message: `${selected.size} produit${selected.size > 1 ? 's seront archivés' : ' sera archivé'} (réversible).`,
      confirmLabel: 'Archiver',
    });
    if (ok) void runBulk((id) => productsApi.archive(id), 'Archivage');
  };

  const bulkDelete = async () => {
    const ok = await confirm({
      title: 'Supprimer la sélection',
      message: `${selected.size} produit${selected.size > 1 ? 's seront supprimés définitivement' : ' sera supprimé définitivement'} (dépublication + archivage + suppression). Action irréversible.`,
      confirmLabel: 'Supprimer définitivement',
      tone: 'danger',
    });
    if (ok)
      void runBulk(async (id) => {
        await productsApi.unpublish(id).catch(() => {});
        await productsApi.archive(id).catch(() => {});
        await productsApi.remove(id);
      }, 'Suppression');
  };

  const applyBulkCategory = () => {
    if (!bulkCatId) {
      toast.error('Choisis une catégorie de destination.');
      return;
    }
    setBulkCatOpen(false);
    void runBulk(
      (id) => productsApi.update(id, { categoryId: bulkCatId }),
      'Changement de catégorie',
    ).then(() => setBulkCatId(''));
  };

  const rootCategories = (categories ?? []).filter((c) => c.level === 1);

  // Map categoryId → category object (utilisé pour afficher emoji+nom dans la grille)
  const catById = useMemo(() => {
    const m = new Map<string, Category>();
    (categories ?? []).forEach((c) => m.set(c.id, c));
    return m;
  }, [categories]);

  return (
    <>
      <PageHead
        title="Catalogue"
        sub={
          stats
            ? `${stats.total} produit${stats.total > 1 ? 's' : ''} · ${stats.published} publié${stats.published > 1 ? 's' : ''} · ${stats.draft} brouillon${stats.draft > 1 ? 's' : ''}`
            : 'Chargement des produits…'
        }
        actions={
          <>
            <AdminButton variant="ghost" onClick={() => void runExport()} disabled={exporting}>
              <AdminIcon n="download" s={14} />
              {exporting ? 'Export…' : 'Export'}
            </AdminButton>
            <AdminButton
              variant="ghost"
              onClick={() => navigate('/catalogue/assistant-ia')}
              style={{
                borderColor: 'var(--a-terracotta)',
                color: 'var(--a-terracotta)',
              }}
              data-testid="open-ai-assistant"
            >
              <AdminIcon n="star" s={14} />
              Assistant IA
            </AdminButton>
            <AdminButton variant="ghost" onClick={() => navigate('/catalogue/revue')}>
              <AdminIcon n="eye" s={14} />
              Relire les brouillons
            </AdminButton>
            <AdminButton variant="primary" onClick={() => navigate('/catalogue/nouveau')}>
              <AdminIcon n="plus" s={14} />
              Nouveau produit
            </AdminButton>
          </>
        }
      />

      <div className="kpi-grid">
        <div className="kpi">
          <div className="kpi-l">Produits publiés</div>
          <div className="kpi-v tn">{stats?.published ?? '—'}</div>
          <span className="kpi-delta neutral">sur {stats?.total ?? '—'} au total</span>
        </div>
        {/* L'action est posée là où le besoin naît : on lit le nombre de
            fiches en attente, on part les relire. */}
        <div className={stats?.draft ? 'kpi warning' : 'kpi'}>
          <div className="kpi-l">Brouillons</div>
          <div className="kpi-v tn">{stats?.draft ?? '—'}</div>
          {stats?.draft ? (
            <button
              type="button"
              className="kpi-action"
              onClick={() => navigate('/catalogue/revue')}
            >
              Les relire
              <AdminIcon n="chevR" s={12} />
            </button>
          ) : (
            <span className="kpi-delta neutral">Aucune fiche en attente</span>
          )}
        </div>
        <div className="kpi accent">
          <div className="kpi-l">En pause</div>
          <div className="kpi-v tn">{stats?.paused ?? '—'}</div>
          <span className="kpi-delta neutral">Masqués du site</span>
        </div>
        <div className="kpi">
          <div className="kpi-l">Archivés</div>
          <div className="kpi-v tn">{stats?.archived ?? '—'}</div>
          <span className="kpi-delta neutral">Historique</span>
        </div>
      </div>

      <Card>
        {/* Barre 1 : recherche + filtres + tri + view toggle */}
        <div className="cat-controls">
          <div className="cat-search">
            <input
              type="search"
              placeholder="Rechercher SKU, nom, marque…"
              value={search}
              onChange={(e) => {
                setPage(1);
                setSearch(e.target.value);
              }}
            />
            <span className="icn">
              <AdminIcon n="search" s={14} />
            </span>
          </div>

          <Dropdown
            id="status"
            label="Statut"
            value={statusFilter}
            options={STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
            openId={openDropdown}
            setOpenId={setOpenDropdown}
            onChange={(v) => {
              setStatusFilter(v as ProductStatus | '');
              setPage(1);
            }}
          />

          <Dropdown
            id="kind"
            label="Type"
            value={kindFilter}
            options={KIND_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
            openId={openDropdown}
            setOpenId={setOpenDropdown}
            onChange={(v) => {
              setKindFilter(v as '' | 'simple' | 'pack');
              setPage(1);
            }}
          />

          <Dropdown
            id="stock"
            label="Stock"
            value={stockFilter}
            options={STOCK_OPTIONS}
            openId={openDropdown}
            setOpenId={setOpenDropdown}
            onChange={(v) => {
              setStockFilter(v);
              setPage(1);
            }}
          />

          {(hubs ?? []).length > 0 && (
            <Dropdown
              id="hub"
              label="Hub"
              value={hubFilter}
              options={[
                { value: '', label: 'Tous les hubs' },
                ...(hubs ?? []).map((h) => ({ value: h.id, label: `${h.code} — ${h.city}` })),
              ]}
              openId={openDropdown}
              setOpenId={setOpenDropdown}
              onChange={(v) => {
                setHubFilter(v);
                setPage(1);
              }}
            />
          )}

          {(brands ?? []).length > 0 && (
            <Dropdown
              id="brand"
              label="Marque"
              value={brandFilter}
              options={[
                { value: '', label: 'Toutes les marques' },
                ...(brands ?? []).map((b) => ({
                  value: b.id,
                  label: `${b.name} (${b.productsCount})`,
                })),
              ]}
              openId={openDropdown}
              setOpenId={setOpenDropdown}
              onChange={(v) => {
                setBrandFilter(v);
                setPage(1);
              }}
            />
          )}

          <div className="spacer" />

          <select
            className="cat-sort"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            aria-label="Tri"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>

          {view === 'grid' && (
            <select
              className="cat-sort"
              value={gridCols}
              onChange={(e) => setGridColsPref(Number(e.target.value))}
              aria-label="Colonnes de la grille"
              title="Nombre de produits par ligne"
            >
              {GRID_COLS_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n} colonnes
                </option>
              ))}
            </select>
          )}

          <select
            className="cat-sort"
            value={pageSize}
            onChange={(e) => {
              setPageSizePref(Number(e.target.value));
              setPage(1);
            }}
            aria-label="Produits par page"
            title="Nombre de produits par page"
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n} / page
              </option>
            ))}
          </select>

          <div className="seg-view" role="tablist" aria-label="Vue catalogue">
            <button
              type="button"
              className={view === 'table' ? 'on' : ''}
              onClick={() => setView('table')}
              aria-label="Vue tableau"
              title="Vue tableau"
            >
              <AdminIcon n="dots" s={14} />
            </button>
            <button
              type="button"
              className={view === 'grid' ? 'on' : ''}
              onClick={() => setView('grid')}
              aria-label="Vue grille"
              title="Vue grille"
            >
              <AdminIcon n="box" s={14} />
            </button>
          </div>
        </div>

        {/* Barre 2 : chips catégories scroll horizontal */}
        {rootCategories.length > 0 && (
          <div
            className="cat-controls"
            style={{
              background: 'var(--a-surface)',
              borderBottom: '1px solid var(--a-border)',
              overflowX: 'auto',
              flexWrap: 'nowrap',
            }}
          >
            <button
              type="button"
              className={'chip-filter' + (categoryFilter === '' ? ' on' : '')}
              onClick={() => {
                setCategoryFilter('');
                setPage(1);
              }}
              style={{ whiteSpace: 'nowrap' }}
            >
              Toutes catégories
              <span className="ct tn">{stats?.total ?? rawItems.length}</span>
            </button>
            {rootCategories.map((c) => (
              <button
                key={c.id}
                type="button"
                className={'chip-filter' + (categoryFilter === c.id ? ' on' : '')}
                onClick={() => {
                  setCategoryFilter(c.id);
                  setPage(1);
                }}
                style={{ whiteSpace: 'nowrap' }}
              >
                {c.iconEmoji ?? '📦'} {c.name}
              </button>
            ))}
          </div>
        )}

        {/* Résultats */}
        <div className="cat-results">
          <strong className="tn">{items.length}</strong> produit
          {items.length > 1 ? 's' : ''} affiché{items.length > 1 ? 's' : ''}
          {categoryFilter && rootCategories.find((c) => c.id === categoryFilter) && (
            <>
              {' '}
              dans <strong>{rootCategories.find((c) => c.id === categoryFilter)?.name}</strong>
            </>
          )}
          {search && (
            <>
              {' '}
              pour « <strong>{search}</strong> »
            </>
          )}
        </div>

        {/* Bandeau d'actions de masse (sélection) */}
        {selected.size > 0 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '10px 14px',
              background: 'var(--a-surface-2)',
              borderTop: '1px solid var(--a-border)',
              borderBottom: '1px solid var(--a-border)',
              flexWrap: 'wrap',
              position: 'sticky',
              top: 0,
              zIndex: 5,
            }}
          >
            {bulkProgress ? (
              <>
                <AdminIcon n="refresh" s={14} />
                <strong className="tn">{bulkProgress.label} en cours…</strong>
                <div className="bar thin" style={{ flex: 1, maxWidth: 360, minWidth: 100 }}>
                  <i
                    style={{
                      width: `${Math.round((bulkProgress.done / Math.max(1, bulkProgress.total)) * 100)}%`,
                    }}
                  />
                </div>
                <strong className="tn" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {bulkProgress.done}/{bulkProgress.total}
                </strong>
              </>
            ) : (
              <>
                <strong className="tn">
                  {selected.size} sélectionné{selected.size > 1 ? 's' : ''}
                </strong>
                <div
                  style={{ width: 1, height: 20, background: 'var(--a-border)', margin: '0 4px' }}
                />
                <AdminButton size="sm" variant="ghost" onClick={addSelectedToMarketing}>
                  <AdminIcon n="trend" s={13} />
                  Panier marketing
                </AdminButton>
                <AdminButton size="sm" variant="ghost" onClick={() => setBulkCatOpen(true)}>
                  <AdminIcon n="tag" s={13} />
                  Changer la catégorie
                </AdminButton>
                <AdminButton size="sm" variant="ghost" onClick={() => void bulkArchive()}>
                  <AdminIcon n="box" s={13} />
                  Archiver
                </AdminButton>
                <AdminButton
                  size="sm"
                  variant="ghost"
                  onClick={() => void bulkDelete()}
                  style={{ color: 'var(--a-danger)' }}
                >
                  <AdminIcon n="alert" s={13} />
                  Supprimer
                </AdminButton>
                <div style={{ flex: 1 }} />
                <AdminButton size="sm" variant="ghost" onClick={clearSelection}>
                  Désélectionner
                </AdminButton>
              </>
            )}
          </div>
        )}

        {/* Contenu : grille ou tableau */}
        {isLoading ? (
          <div className="empty" style={{ padding: 60 }}>
            Chargement…
          </div>
        ) : items.length === 0 ? (
          <div className="empty" style={{ padding: 60 }}>
            Aucun produit trouvé pour ces filtres.
          </div>
        ) : view === 'grid' ? (
          <div
            className="pg-grid"
            style={{ gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))` }}
          >
            {items.map((p) => {
              const badge = statusToBadge(p.status);
              const stk = productTotalStock(p);
              const sc = stockClass(stk);
              const price = productEffectivePrice(p);
              const hero = p.mediaLinks?.[0]?.url ?? p.variants?.[0]?.mediaLinks?.[0]?.url ?? null;
              const cat = catById.get(p.categoryId);
              return (
                <div
                  key={p.id}
                  className="pg-card"
                  onClick={() => navigate(`/catalogue/${p.id}`)}
                  data-testid={`pg-card-${p.sku}`}
                >
                  <div className="pg-thumb">
                    <input
                      type="checkbox"
                      className="pg-check"
                      aria-label="Sélectionner"
                      checked={selected.has(p.id)}
                      onChange={() => toggleOne(p.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span className="pg-status">
                      <Tag variant={badge.cls}>{badge.l}</Tag>
                      {p.kind === 'pack' && (
                        <Tag variant="terra" noDot>
                          Pack
                        </Tag>
                      )}
                    </span>
                    {cat && (
                      <span className="pg-cat-tag">
                        {cat.iconEmoji ?? '📦'} {cat.name}
                      </span>
                    )}
                    <button
                      type="button"
                      className="pg-fav"
                      aria-label="Favori"
                      title="Ajouter aux favoris"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <AdminIcon n="star" s={14} />
                    </button>
                    {hero ? (
                      <img src={hero} alt={p.title} loading="lazy" />
                    ) : (
                      <ProdThumb color={colorFromSku(p.sku)} />
                    )}
                  </div>
                  <div className="pg-body">
                    <span className="pg-vendor">{p.brandRef?.name ?? p.brand ?? 'Akwaba'}</span>
                    <span className="pg-name">{p.title}</span>
                    <span className="pg-sku">{p.sku}</span>
                    <div className="pg-foot">
                      <span className="pg-price tn">{fmtFCFA(price)}</span>
                      <span className={`pg-stock ${sc}`}>{stockLabel(stk)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="table-wrap" style={{ margin: 0 }}>
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 24 }}>
                    <input
                      type="checkbox"
                      aria-label="Tout sélectionner"
                      checked={allVisibleSelected}
                      onChange={toggleAllVisible}
                    />
                  </th>
                  <th>SKU / Produit</th>
                  <th>Marque</th>
                  <th>Catégorie</th>
                  <th className="right">Prix</th>
                  <th className="right">Stock</th>
                  <th>Statut</th>
                  <th>Mise à jour</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => {
                  const badge = statusToBadge(p.status);
                  const cat = catById.get(p.categoryId);
                  const stock = productTotalStock(p);
                  const price = productEffectivePrice(p);
                  const hero =
                    p.mediaLinks?.[0]?.url ?? p.variants?.[0]?.mediaLinks?.[0]?.url ?? null;
                  return (
                    <tr
                      key={p.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/catalogue/${p.id}`)}
                    >
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          aria-label="Sélectionner"
                          checked={selected.has(p.id)}
                          onChange={() => toggleOne(p.id)}
                        />
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div
                            style={{
                              width: 36,
                              height: 36,
                              borderRadius: 6,
                              background: 'var(--a-surface-2)',
                              display: 'grid',
                              placeItems: 'center',
                              flexShrink: 0,
                              overflow: 'hidden',
                            }}
                          >
                            {hero ? (
                              <img
                                src={hero}
                                alt=""
                                style={{ width: 36, height: 36, objectFit: 'cover' }}
                              />
                            ) : (
                              <AdminIcon n="box" s={16} />
                            )}
                          </div>
                          <div>
                            <strong>{p.title}</strong>
                            {p.kind === 'pack' && (
                              <>
                                {' '}
                                <Tag variant="terra" noDot>
                                  Pack
                                </Tag>
                              </>
                            )}
                            <div className="mono muted" style={{ fontSize: 11 }}>
                              {p.sku}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>{p.brandRef?.name ?? p.brand ?? '—'}</td>
                      <td>
                        <Tag variant="neutral" noDot>
                          {cat?.iconEmoji ?? '📦'} {cat?.name ?? '—'}
                        </Tag>
                      </td>
                      <td className="right tn">
                        <strong>{fmtFCFA(price)}</strong>
                      </td>
                      <td className="right tn">
                        <span className={`pg-stock ${stockClass(stock)}`} style={{ fontSize: 12 }}>
                          {stock}
                        </span>
                      </td>
                      <td>
                        <Tag variant={badge.cls}>{badge.l}</Tag>
                      </td>
                      <td className="muted" style={{ fontSize: 11 }}>
                        {new Date(p.updatedAt).toLocaleDateString('fr-FR')}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="top-btn"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <AdminIcon n="dots" s={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {total > 0 && (
          <Pagination
            from={from}
            to={to}
            total={total}
            current={page}
            totalPages={totalPages}
            onChange={setPage}
          />
        )}
      </Card>

      {bulkCatOpen && (
        <Modal
          title="Changer la catégorie"
          subtitle={`${selected.size} produit${selected.size > 1 ? 's seront déplacés' : ' sera déplacé'} vers la catégorie choisie.`}
          onClose={() => setBulkCatOpen(false)}
          width={520}
        >
          <CategoryPicker
            inline
            categories={categories ?? []}
            value={bulkCatId}
            onChange={setBulkCatId}
          />
          <ModalFooter>
            <AdminButton variant="ghost" onClick={() => setBulkCatOpen(false)}>
              Annuler
            </AdminButton>
            <AdminButton
              variant="primary"
              onClick={applyBulkCategory}
              disabled={!bulkCatId || bulkBusy}
            >
              Déplacer {selected.size} produit{selected.size > 1 ? 's' : ''}
            </AdminButton>
          </ModalFooter>
        </Modal>
      )}
    </>
  );
}
